import { Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import { RequireOrgOwner, RequireOrgManager, RequireOrganization } from "@/components/common/OrgGate";
import { ProtectedRoute } from "@/components/common/ProtectedRoute";
import { AcceptOrgInvitationPage } from "@/pages/AcceptOrgInvitationPage";
import { LoginPage } from "@/pages/LoginPage";
import { ForgotPasswordPage } from "@/pages/ForgotPasswordPage";
import { ResetPasswordPage } from "@/pages/ResetPasswordPage";
import { AppDashboard } from "@/pages/AppDashboard";
import { ComingSoonPage } from "@/pages/ComingSoonPage";
import { OrgUsersPage } from "@/pages/OrgUsersPage";
import { OrgRolesPage } from "@/pages/OrgRolesPage";
import { OrgIntegrationsPage } from "@/pages/OrgIntegrationsPage";
import { GoogleDriveIntegrationPage } from "@/pages/integrations/GoogleDriveIntegrationPage";
import { EmailIntegrationPage } from "@/pages/integrations/EmailIntegrationPage";
import { OrgUserDetailPage } from "@/pages/OrgUserDetailPage";
import { NewReviewPage } from "@/apps/contract-review/pages/NewReviewPage";
import { ContractReviewPage } from "@/apps/contract-review/pages/ContractReviewPage";
import { ContractTypesPage } from "@/apps/contract-review/pages/ContractTypesPage";
import { RolesPage } from "@/apps/contract-review/pages/RolesPage";
import { DashboardHome } from "@/apps/contract-review/pages/DashboardHome";
import { DraftingHome } from "@/apps/drafting/pages/DraftingHome";
import { NewDraftPage } from "@/apps/drafting/pages/NewDraftPage";
import { DraftWorkspacePage } from "@/apps/drafting/pages/DraftWorkspacePage";
import { DraftReviewPage } from "@/apps/drafting/pages/DraftReviewPage";
import { DraftExportPage } from "@/apps/drafting/pages/DraftExportPage";
import { DraftTemplatesPage } from "@/apps/drafting/pages/DraftTemplatesPage";
import { DraftLegalRolesPage } from "@/apps/drafting/pages/DraftLegalRolesPage";
import { DraftClauseLibraryPage } from "@/apps/drafting/pages/DraftClauseLibraryPage";
import {
  RagLayout,
  WorkspacesPage,
  WorkspaceDetailPage,
  WorkspaceMembersPage,
  WorkspaceChatListPage,
  WorkspaceChatPage,
  LibraryDocumentsPage,
  DocumentViewerPage,
} from "@/apps/rag";
import {
  RegulatoryLayout,
  RegulatoryLibrariesPage,
  RegulatoryGlobalChatListPage,
  RegulatoryGlobalChatPage,
  PublicLibraryDocumentsPage,
} from "@/apps/regulatory";
import { ResearchPage } from "@/apps/research/pages/ResearchPage";
import { SupervisorChatPage } from "@/apps/chat/pages/SupervisorChatPage";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { ComingSoon } from "@/apps/_shared/ComingSoon";
import { PermissionRouteGate } from "@/components/common/PermissionRouteGate";
import { APPS } from "@/lib/apps";
import { usePermission } from "@/contexts/AuthContext";

function AppRouteGate({ appId, children }: { appId: string; children: React.ReactNode }) {
  const allowed = usePermission("app", appId);
  const app = APPS.find((entry) => entry.id === appId);
  if (!allowed) {
    return (
      <ComingSoon
        title={app?.name ?? "Application"}
        description={app?.description ?? "This application is not enabled for your organization."}
      />
    );
  }
  return <>{children}</>;
}

function LegacyRegulatoryRedirect() {
  const { pathname } = useLocation();
  return <Navigate to={pathname.replace("/rag/regulatory", "/regulatory")} replace />;
}

function LegacyGoogleDriveRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/settings/google-drive${search}`} replace />;
}

function LibraryDocumentsRedirect() {
  const { workspaceId, libraryId } = useParams();
  return (
    <Navigate
      to={`/rag/workspaces/${workspaceId}/libraries/${libraryId}/documents`}
      replace
    />
  );
}

function RegulatoryLibraryDocumentsRedirect() {
  const { libraryId } = useParams();
  return <Navigate to={`/regulatory/libraries/${libraryId}/documents`} replace />;
}

function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<Navigate to="/login" replace />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/accept-invitation/:invitationId" element={<AcceptOrgInvitationPage />} />
      <Route path="/invitation/:token" element={<Navigate to="/" replace />} />
      <Route element={<ProtectedRoute />}>
        <Route element={<RequireOrganization />}>
          <Route element={<DashboardLayout />}>
            <Route
              index
              element={
                <AppRouteGate appId="chat">
                  <SupervisorChatPage />
                </AppRouteGate>
              }
            />
            <Route path="/home" element={<AppDashboard />} />
            <Route path="/coming-soon/:slug" element={<ComingSoonPage />} />
            <Route
              path="/contract-review"
              element={
                <AppRouteGate appId="contract-review">
                  <PermissionRouteGate resource="review" action="read">
                    <DashboardHome />
                  </PermissionRouteGate>
                </AppRouteGate>
              }
            />
            <Route
              path="/contract-review/new"
              element={
                <AppRouteGate appId="contract-review">
                  <PermissionRouteGate resource="review" action="create">
                    <NewReviewPage />
                  </PermissionRouteGate>
                </AppRouteGate>
              }
            />
            <Route
              path="/contract-review/:id"
              element={
                <AppRouteGate appId="contract-review">
                  <PermissionRouteGate resource="review" action="read">
                    <ContractReviewPage />
                  </PermissionRouteGate>
                </AppRouteGate>
              }
            />
            <Route
              path="/contract-review/contract-types"
              element={
                <AppRouteGate appId="contract-review">
                  <PermissionRouteGate resource="contractType" action="read">
                    <ContractTypesPage />
                  </PermissionRouteGate>
                </AppRouteGate>
              }
            />
            <Route
              path="/contract-review/roles"
              element={
                <AppRouteGate appId="contract-review">
                  <PermissionRouteGate resource="customRole" action="read">
                    <RolesPage />
                  </PermissionRouteGate>
                </AppRouteGate>
              }
            />
            <Route path="/contract-review/users" element={<Navigate to="/users" replace />} />
            <Route
              path="/users"
              element={
                <RequireOrgOwner>
                  <OrgUsersPage />
                </RequireOrgOwner>
              }
            />
            <Route
              path="/users/:userId"
              element={
                <RequireOrgOwner>
                  <OrgUserDetailPage />
                </RequireOrgOwner>
              }
            />
            <Route
              path="/organization/roles"
              element={
                <RequireOrgOwner>
                  <OrgRolesPage />
                </RequireOrgOwner>
              }
            />
            <Route
              path="/organization/integrations"
              element={
                <RequireOrgManager>
                  <OrgIntegrationsPage />
                </RequireOrgManager>
              }
            />
            <Route
              path="/settings/google-drive"
              element={
                <RequireOrganization>
                  <GoogleDriveIntegrationPage />
                </RequireOrganization>
              }
            />
            <Route
              path="/settings/email"
              element={
                <RequireOrganization>
                  <EmailIntegrationPage />
                </RequireOrganization>
              }
            />
            <Route
              path="/organization/integrations/google-drive"
              element={<LegacyGoogleDriveRedirect />}
            />
            <Route
              path="/drafting"
              element={
                <AppRouteGate appId="drafting">
                  <PermissionRouteGate resource="draft" action="read">
                    <DraftingHome />
                  </PermissionRouteGate>
                </AppRouteGate>
              }
            />
            <Route
              path="/drafting/new"
              element={
                <AppRouteGate appId="drafting">
                  <PermissionRouteGate resource="draft" action="create">
                    <NewDraftPage />
                  </PermissionRouteGate>
                </AppRouteGate>
              }
            />
            <Route
              path="/drafting/contract-types"
              element={<Navigate to="/drafting/templates" replace />}
            />
            <Route
              path="/drafting/templates"
              element={
                <AppRouteGate appId="drafting">
                  <PermissionRouteGate resource="draftTemplate" action="read">
                    <DraftTemplatesPage />
                  </PermissionRouteGate>
                </AppRouteGate>
              }
            />
            <Route
              path="/drafting/legal-roles"
              element={
                <AppRouteGate appId="drafting">
                  <PermissionRouteGate resource="draftLegalRole" action="read">
                    <DraftLegalRolesPage />
                  </PermissionRouteGate>
                </AppRouteGate>
              }
            />
            <Route
              path="/drafting/clause-library"
              element={
                <AppRouteGate appId="drafting">
                  <PermissionRouteGate resource="draftClause" action="read">
                    <DraftClauseLibraryPage />
                  </PermissionRouteGate>
                </AppRouteGate>
              }
            />
            <Route
              path="/drafting/:id/review"
              element={
                <AppRouteGate appId="drafting">
                  <PermissionRouteGate resource="draft" action="read">
                    <DraftReviewPage />
                  </PermissionRouteGate>
                </AppRouteGate>
              }
            />
            <Route
              path="/drafting/:id/export"
              element={
                <AppRouteGate appId="drafting">
                  <PermissionRouteGate resource="draft" action="export">
                    <DraftExportPage />
                  </PermissionRouteGate>
                </AppRouteGate>
              }
            />
            <Route
              path="/drafting/:id"
              element={
                <AppRouteGate appId="drafting">
                  <PermissionRouteGate resource="draft" action="read">
                    <DraftWorkspacePage />
                  </PermissionRouteGate>
                </AppRouteGate>
              }
            />
            <Route
              path="/regulatory"
              element={
                <AppRouteGate appId="regulatory">
                  <RegulatoryLayout />
                </AppRouteGate>
              }
            >
              <Route index element={<Navigate to="chat" replace />} />
              <Route path="chat" element={<RegulatoryGlobalChatListPage />} />
              <Route path="chat/:chatId" element={<RegulatoryGlobalChatPage />} />
              <Route path="libraries" element={<RegulatoryLibrariesPage />} />
              <Route
                path="libraries/:libraryId/documents"
                element={<PublicLibraryDocumentsPage />}
              />
              <Route
                path="libraries/:libraryId/documents/:documentId/view"
                element={<DocumentViewerPage />}
              />
              <Route path="libraries/:libraryId/chats" element={<RegulatoryLibraryDocumentsRedirect />} />
              <Route
                path="libraries/:libraryId/chats/:chatId"
                element={<RegulatoryLibraryDocumentsRedirect />}
              />
            </Route>
            <Route path="/rag/regulatory/*" element={<LegacyRegulatoryRedirect />} />
            <Route
              path="/rag"
              element={
                <AppRouteGate appId="rag">
                  <PermissionRouteGate resource="kmWorkspace" action="read">
                    <RagLayout />
                  </PermissionRouteGate>
                </AppRouteGate>
              }
            >
              <Route index element={<WorkspacesPage />} />
              <Route path="workspaces/:workspaceId" element={<WorkspaceDetailPage />} />
              <Route path="workspaces/:workspaceId/members" element={<WorkspaceMembersPage />} />
              <Route path="workspaces/:workspaceId/chat" element={<WorkspaceChatListPage />} />
              <Route path="workspaces/:workspaceId/chat/:chatId" element={<WorkspaceChatPage />} />
              <Route
                path="workspaces/:workspaceId/libraries/:libraryId/documents"
                element={<LibraryDocumentsPage />}
              />
              <Route
                path="workspaces/:workspaceId/libraries/:libraryId/documents/:documentId/view"
                element={<DocumentViewerPage />}
              />
              <Route
                path="workspaces/:workspaceId/libraries/:libraryId/chats"
                element={<LibraryDocumentsRedirect />}
              />
              <Route
                path="workspaces/:workspaceId/libraries/:libraryId/chats/:chatId"
                element={<LibraryDocumentsRedirect />}
              />
            </Route>
            <Route path="/research" element={<AppRouteGate appId="research"><ResearchPage /></AppRouteGate>} />
            <Route
              path="/chat/:conversationId?"
              element={
                <AppRouteGate appId="chat">
                  <SupervisorChatPage />
                </AppRouteGate>
              }
            />
          </Route>
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
